import apiClient from '../apiClient';
import type { BaseResponse } from '../../types/base.dto';

export type CashflowDimension = 'day' | 'week' | 'month';
export type CashflowDataType = 'daily' | 'monthly';

export interface CashflowDataRequest {
  startDate: string;
  endDate: string;
  storeId?: number;
  dimension?: CashflowDimension;
}

export interface CashflowSummary {
  totalCapitalInjection: number;
  totalPrepaidProfit: number;
  totalReceivableProfit: number;
  totalAccumulatedProfit: number;
}

export interface ReceivedRentData {
  basicRent: number;
  prepaidRent: number;
  overdueRecovery: number;
  badDebtRecovery: number;
}

export interface IncomeData {
  receivedRent: ReceivedRentData;
  penaltyIncome: number;
  buyoutIncome: number;
  disposalIncome: number;
}

export interface ReceivableData {
  devicePurchaseFund: number;
  receivableIncome: number;
  inventoryResidualValue: number;
}

export interface ManagementFee {
  electricityFee: number;
  rentFee: number;
  propertyFee: number;
  laborCost: number;
}

export interface ExpenseData {
  commissionExpense: number;
  purchaseCost: number;
  managementFee: ManagementFee;
}

export interface InventoryTurnover {
  disposalIncome: number;
  inventoryResidualValue: number;
}

export interface PeriodItems {
  capitalInjection: number;
  income: IncomeData;
  receivable: ReceivableData;
  expense: ExpenseData;
  inventoryTurnover: InventoryTurnover;
  badDebtProvision: number;
  balance: number;
}

export interface PeriodData {
  period: string;
  periodLabel: string;
  updateStatus?: string;
  items: PeriodItems;
}

export interface CashflowDataResponse {
  summary: CashflowSummary;
  periods: PeriodData[];
}

export interface CashflowManualDataRequest {
  recordDate: string;
  storeId?: number;
  dataType: CashflowDataType;
  capitalInjection?: number;
  penaltyIncome?: number;
  buyoutIncome?: number;
  disposalIncome?: number;
  devicePurchaseFund?: number;
  inventoryResidualValue?: number;
  purchaseCost?: number;
  electricityFee?: number;
  rentFee?: number;
  propertyFee?: number;
  laborCost?: number;
  remark?: string;
}

export interface CashflowManualData {
  id: number;
  recordDate: string;
  storeId?: number;
  dataType: CashflowDataType;
  capitalInjection: number;
  penaltyIncome: number;
  buyoutIncome: number;
  disposalIncome: number;
  devicePurchaseFund: number;
  inventoryResidualValue: number;
  purchaseCost: number;
  electricityFee: number;
  rentFee: number;
  propertyFee: number;
  laborCost: number;
  remark?: string;
  creatorId: number;
  creatorName: string;
  createdAt: string;
  updatedAt: string;
}

export interface ListPagination {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

export interface CashflowSyncRequest {
  startDate: string;
  endDate: string;
  storeId?: number;
}

export interface CashflowSyncResponse {
  message: string;
  updatedRows: number;
}

class CashflowDataAPI {
  async getStatistics(params: CashflowDataRequest): Promise<BaseResponse<CashflowDataResponse>> {
    const response = await apiClient.post<BaseResponse<CashflowDataResponse>>('/cashflow-data/statistics', params);
    return response.data;
  }

  async addManualData(params: CashflowManualDataRequest): Promise<BaseResponse<CashflowManualData>> {
    const response = await apiClient.post<BaseResponse<CashflowManualData>>('/cashflow-data/manual', params);
    return response.data;
  }

  async listManualData(
    params: unknown
  ): Promise<BaseResponse<{ items: CashflowManualData[]; pagination: ListPagination }>> {
    const response = await apiClient.post<BaseResponse<{ items: CashflowManualData[]; pagination: ListPagination }>>(
      '/cashflow-data/manual/list',
      params
    );
    return response.data;
  }

  async deleteManualData(id: number): Promise<BaseResponse<string>> {
    const response = await apiClient.delete<BaseResponse<string>>(`/cashflow-data/manual/${id}`);
    return response.data;
  }

  async syncSystemData(params: CashflowSyncRequest): Promise<BaseResponse<CashflowSyncResponse>> {
    const response = await apiClient.post<BaseResponse<CashflowSyncResponse>>('/cashflow-data/sync', params);
    return response.data;
  }
}

export const cashflowDataApi = new CashflowDataAPI();
export default cashflowDataApi;
